import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, Edit2, Save, X, Repeat, CalendarDays } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const CATEGORIES = ["Loyer/Dépôt", "Publicité", "Abonnement", "Assurance", "Transport", "Fournitures", "Autre"];

const Charges = () => {
  const { token } = useAuth();
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMois, setSelectedMois] = useState(new Date().toISOString().slice(0, 7));
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [activeTab, setActiveTab] = useState("recurrente");
  const [annualStats, setAnnualStats] = useState({ total_annuel: 0, mensuel_equivalent: 0, count: 0 });

  const [formData, setFormData] = useState({
    libelle: "", montant_ttc: "", mois: new Date().toISOString().slice(0, 7),
    type_charge: "recurrente", categorie: "", date_charge: "",
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCharges = async () => {
    try {
      const res = await axios.get(`${API}/charges?mois=${selectedMois}`, { headers });
      setCharges(res.data);
    } catch { toast.error("Erreur chargement charges"); }
    finally { setLoading(false); }
  };

  const fetchAnnualStats = async () => {
    try {
      const annee = selectedMois.slice(0, 4);
      const res = await axios.get(`${API}/charges/annuelles-stats?annee=${annee}`, { headers });
      setAnnualStats(res.data);
    } catch { /* silent */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/charges`, {
        ...formData,
        montant_ttc: parseFloat(formData.montant_ttc),
        mois: selectedMois,
        type_charge: formData.type_charge,
        categorie: formData.categorie,
        date_charge: formData.type_charge !== "recurrente" ? formData.date_charge : null,
      }, { headers });
      toast.success("Charge ajoutée !");
      setDialogOpen(false);
      setFormData({ libelle: "", montant_ttc: "", mois: selectedMois, type_charge: "recurrente", categorie: "", date_charge: "" });
      fetchCharges();
      fetchAnnualStats();
    } catch { toast.error("Erreur lors de l'ajout"); }
  };

  const handleUpdate = async (chargeId, newMontant) => {
    try {
      const charge = charges.find(c => c.id === chargeId);
      await axios.put(`${API}/charges/${chargeId}`, {
        libelle: charge.libelle, montant_ttc: parseFloat(newMontant), mois: charge.mois,
        type_charge: charge.type_charge || "recurrente", categorie: charge.categorie || "",
        date_charge: charge.date_charge || null,
      }, { headers });
      toast.success("Charge mise à jour !");
      setEditingId(null);
      fetchCharges();
    } catch { toast.error("Erreur mise à jour"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette charge ?")) return;
    try { await axios.delete(`${API}/charges/${id}`, { headers }); toast.success("Supprimé"); fetchCharges(); fetchAnnualStats(); }
    catch { toast.error("Erreur suppression"); }
  };

  const handleInitDefaultCharges = async () => {
    const defaults = [
      { libelle: "Dépôt", montant_ttc: 1400, categorie: "Loyer/Dépôt" },
      { libelle: "Shopify", montant_ttc: 29, categorie: "Abonnement" },
      { libelle: "Wifi", montant_ttc: 70, categorie: "Abonnement" },
      { libelle: "Assurance", montant_ttc: 50, categorie: "Assurance" },
      { libelle: "Pub Facebook", montant_ttc: 200, categorie: "Publicité" },
    ];
    try {
      for (const c of defaults) {
        await axios.post(`${API}/charges`, { ...c, mois: selectedMois, type_charge: "recurrente" }, { headers });
      }
      toast.success("Charges par défaut créées !");
      fetchCharges();
    } catch { toast.error("Erreur initialisation"); }
  };

  useEffect(() => { fetchCharges(); fetchAnnualStats(); }, [selectedMois]);

  const recurrentes = charges.filter(c => (c.type_charge || "recurrente") === "recurrente");
  const occasionnelles = charges.filter(c => c.type_charge === "occasionnelle");
  const annuelles = charges.filter(c => c.type_charge === "annuelle");
  const displayed = activeTab === "recurrente" ? recurrentes : activeTab === "occasionnelle" ? occasionnelles : annuelles;
  const totalRecurrentes = recurrentes.reduce((a, c) => a + c.montant_ttc, 0);
  const totalOccasionnelles = occasionnelles.reduce((a, c) => a + c.montant_ttc, 0);
  const totalAnnuelles = annuelles.reduce((a, c) => a + c.montant_ttc, 0);
  const totalAll = totalRecurrentes + totalOccasionnelles + totalAnnuelles;
  const isDated = activeTab === "occasionnelle" || activeTab === "annuelle";

  const renderTable = (items) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Libellé</TableHead>
          <TableHead>Catégorie</TableHead>
          {isDated && <TableHead>Date</TableHead>}
          <TableHead className="text-right">Montant TTC (€)</TableHead>
          {activeTab === "annuelle" && <TableHead className="text-right">≈ /mois</TableHead>}
          <TableHead className="w-32"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((charge) => (
          <TableRow key={charge.id} data-testid={`charge-row-${charge.id}`}>
            <TableCell className="font-medium">{charge.libelle}</TableCell>
            <TableCell>
              {charge.categorie ? <Badge variant="outline" className="text-xs">{charge.categorie}</Badge> : <span className="text-slate-400">—</span>}
            </TableCell>
            {isDated && (
              <TableCell className="text-sm">{charge.date_charge || "—"}</TableCell>
            )}
            <TableCell className="text-right">
              {editingId === charge.id ? (
                <Input type="number" step="0.01" value={editValue} onChange={e => setEditValue(e.target.value)} className="w-28 text-right ml-auto" autoFocus data-testid={`edit-charge-input-${charge.id}`} />
              ) : (
                <span className="font-medium">{charge.montant_ttc.toFixed(2)} €</span>
              )}
            </TableCell>
            {activeTab === "annuelle" && (
              <TableCell className="text-right text-sm text-blue-600" data-testid={`charge-monthly-${charge.id}`}>{(charge.montant_ttc / 12).toFixed(2)} €</TableCell>
            )}
            <TableCell>
              <div className="flex items-center justify-end gap-1">
                {editingId === charge.id ? (
                  <>
                    <Button size="sm" onClick={() => handleUpdate(charge.id, editValue)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`save-charge-${charge.id}`}><Save className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(charge.id); setEditValue(charge.montant_ttc.toString()); }} data-testid={`edit-charge-${charge.id}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(charge.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-charge-${charge.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
        <TableRow className="bg-slate-50 font-semibold">
          <TableCell>TOTAL</TableCell>
          <TableCell></TableCell>
          {isDated && <TableCell></TableCell>}
          <TableCell className="text-right text-red-600">{items.reduce((a, c) => a + c.montant_ttc, 0).toFixed(2)} €</TableCell>
          {activeTab === "annuelle" && <TableCell className="text-right text-blue-600">{(items.reduce((a, c) => a + c.montant_ttc, 0) / 12).toFixed(2)} €</TableCell>}
          <TableCell></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="charges-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Charges</h1>
          <p className="text-slate-500 mt-1">Charges récurrentes et occasionnelles</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-charge-btn"><Plus className="w-4 h-4 mr-2" />Nouvelle charge</Button>
          </DialogTrigger>
          <DialogContent data-testid="charge-dialog">
            <DialogHeader><DialogTitle>Ajouter une charge</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.type_charge} onValueChange={v => setFormData({ ...formData, type_charge: v })}>
                  <SelectTrigger data-testid="type-charge-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recurrente">Récurrente (mensuelle)</SelectItem>
                    <SelectItem value="occasionnelle">Occasionnelle (ponctuelle)</SelectItem>
                    <SelectItem value="annuelle">Annuelle (1×/an, lissée en mensuel)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Libellé</Label>
                <Input value={formData.libelle} onChange={e => setFormData({ ...formData, libelle: e.target.value })} placeholder="Ex: Pub Facebook" required data-testid="libelle-input" />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={formData.categorie} onValueChange={v => setFormData({ ...formData, categorie: v })}>
                  <SelectTrigger data-testid="categorie-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Montant TTC (€)</Label>
                <Input type="number" step="0.01" value={formData.montant_ttc} onChange={e => setFormData({ ...formData, montant_ttc: e.target.value })} placeholder="0.00" required data-testid="montant-input" />
              </div>
              {(formData.type_charge === "occasionnelle" || formData.type_charge === "annuelle") && (
                <div className="space-y-2">
                  <Label>{formData.type_charge === "annuelle" ? "Date de paiement (imputée sur ce mois)" : "Date de la charge"}</Label>
                  <Input type="date" value={formData.date_charge} onChange={e => setFormData({ ...formData, date_charge: e.target.value })} data-testid="date-charge-input" />
                  {formData.type_charge === "annuelle" && formData.montant_ttc && (
                    <p className="text-xs text-blue-600">≈ {(parseFloat(formData.montant_ttc) / 12).toFixed(2)} €/mois sur l'année</p>
                  )}
                </div>
              )}
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-charge-btn">Ajouter</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Month selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-slate-600">Mois :</Label>
              <Input type="month" value={selectedMois} onChange={e => setSelectedMois(e.target.value)} className="w-48" data-testid="mois-select" />
            </div>
            {charges.length === 0 && (
              <Button variant="outline" onClick={handleInitDefaultCharges} data-testid="init-charges-btn">Initialiser charges par défaut</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Récurrentes</p><p className="text-2xl font-bold text-slate-800">{totalRecurrentes.toFixed(2)} €</p><p className="text-xs text-slate-400">{recurrentes.length} postes</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Occasionnelles</p><p className="text-2xl font-bold text-amber-600">{totalOccasionnelles.toFixed(2)} €</p><p className="text-xs text-slate-400">{occasionnelles.length} postes</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Annuelles (payées ce mois)</p><p className="text-2xl font-bold text-purple-600">{totalAnnuelles.toFixed(2)} €</p><p className="text-xs text-slate-400">{annuelles.length} postes</p></CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4"><p className="text-sm text-red-600">Total ce mois</p><p className="text-2xl font-bold text-red-700">{totalAll.toFixed(2)} €</p></CardContent></Card>
      </div>

      {/* Coût mensuel équivalent des charges annuelles */}
      <Card className="border-purple-200 bg-purple-50/50" data-testid="annual-equiv-card">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-purple-800">Coût mensuel équivalent des charges annuelles ({annualStats.annee || selectedMois.slice(0,4)})</p>
            <p className="text-xs text-purple-600">Total annuel {Number(annualStats.total_annuel || 0).toFixed(2)} € ÷ 12 — {annualStats.count || 0} charge(s) annuelle(s)</p>
          </div>
          <p className="text-3xl font-bold text-purple-700" data-testid="annual-monthly-equiv">≈ {Number(annualStats.mensuel_equivalent || 0).toFixed(2)} €/mois</p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button variant={activeTab === "recurrente" ? "default" : "outline"} onClick={() => setActiveTab("recurrente")}
          className={activeTab === "recurrente" ? "bg-indigo-600" : ""} data-testid="tab-recurrente">
          <Repeat className="w-4 h-4 mr-2" />Récurrentes ({recurrentes.length})
        </Button>
        <Button variant={activeTab === "occasionnelle" ? "default" : "outline"} onClick={() => setActiveTab("occasionnelle")}
          className={activeTab === "occasionnelle" ? "bg-indigo-600" : ""} data-testid="tab-occasionnelle">
          <CalendarDays className="w-4 h-4 mr-2" />Occasionnelles ({occasionnelles.length})
        </Button>
        <Button variant={activeTab === "annuelle" ? "default" : "outline"} onClick={() => setActiveTab("annuelle")}
          className={activeTab === "annuelle" ? "bg-indigo-600" : ""} data-testid="tab-annuelle">
          <CalendarDays className="w-4 h-4 mr-2" />Annuelles ({annuelles.length})
        </Button>
      </div>

      {/* Table */}
      <Card data-testid="charges-table-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-600" />
            {activeTab === "recurrente" ? "Charges récurrentes" : activeTab === "occasionnelle" ? "Charges occasionnelles" : "Charges annuelles"} — {selectedMois}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : displayed.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune charge {activeTab === "recurrente" ? "récurrente" : activeTab === "occasionnelle" ? "occasionnelle" : "annuelle"} pour ce mois</p>
            </div>
          ) : renderTable(displayed)}
        </CardContent>
      </Card>
    </div>
  );
};

export default Charges;
